import { type TFunction } from 'i18next'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { type GetOrder } from '#app/models/ecommerce-providers/sylius/shop/fetch/order/get-order.ts'
import { type GetOrderItem } from '#app/models/ecommerce-providers/sylius/shop/fetch/order-item/get-order-item.ts'
import { type GetProduct } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import { type GetProductVariant } from '#app/models/ecommerce-providers/sylius/shop/fetch/product-variant/get-product-variant.ts'
import { CART_I18NEXT } from '#app/routes/($lang).cart/route-config'

interface CartState {
	order: GetOrder
	itemsCount: number
	pending: {
		action: CART_ACTION_TYPES
		code: GetProductVariant['code']
	}[]
}

interface AddProductToCartAction {
	type: CART_ACTION_TYPES.ADD_PRODUCT_TO_CART
	payload: {
		variant: GetProductVariant
		quantity?: number
	}
}

interface RemoveItemFromCartAction {
	type: CART_ACTION_TYPES.REMOVE_ITEM_FROM_CART
	payload: Pick<GetOrderItem, 'id'>
}

interface UpdateItemCartAction {
	type: CART_ACTION_TYPES.UPDATE_ITEM_FROM_CART
	payload: Pick<GetOrderItem, 'id' | 'quantity'>
}

interface SetCartAction {
	type: CART_ACTION_TYPES.SET_CART
	payload: GetOrder
}

type CartAction =
	| AddProductToCartAction
	| RemoveItemFromCartAction
	| UpdateItemCartAction
	| SetCartAction

enum CART_ACTION_TYPES {
	ADD_PRODUCT_TO_CART = 'ADD_PRODUCT_TO_CART',
	REMOVE_ITEM_FROM_CART = 'REMOVE_ITEM_FROM_CART',
	UPDATE_ITEM_FROM_CART = 'UPDATE_ITEM_FROM_CART',
	SET_CART = 'SET_CART',
}

const cartReducer = (state: CartState, action: CartAction): CartState => {
	let order = {
		...state.order,
	}
	let pending: CartState['pending'] = []

	switch (action.type) {
		case CART_ACTION_TYPES.ADD_PRODUCT_TO_CART:
			const itemAdd = state.order.items?.find(
				(item) =>
					(item.variant as GetProductVariant).code ===
					action.payload.variant.code,
			)
			const quantityAdd =
				(itemAdd?.quantity || 0) + (action.payload.quantity || 1)

			order = {
				...order,
				items: [
					...(!itemAdd
						? [
								{
									variant: action.payload.variant,
									productName:
										action.payload.variant.name ||
										(action.payload.variant.product as GetProduct).name,
									unitPrice: action.payload.variant.price,
									quantity: quantityAdd,
									total: action.payload.variant.price! * quantityAdd,
								},
							]
						: []),
					...(state.order.items ?? []).map((item) => {
						if (
							(item.variant as GetProductVariant).code ===
							action.payload.variant.code
						) {
							return {
								...item,
								quantity: quantityAdd,
								total: action.payload.variant.price! * quantityAdd,
							}
						}
						return item
					}),
				],
			}
			pending.push({
				action: CART_ACTION_TYPES.ADD_PRODUCT_TO_CART,
				code: action.payload.variant.code,
			})

			break
		case CART_ACTION_TYPES.REMOVE_ITEM_FROM_CART:
			order = {
				...order,
				items:
					state.order.items?.filter((item) => {
						if (item.id !== action.payload.id) {
							return true
						}
						if (item.id === action.payload.id) {
							pending.push({
								action: CART_ACTION_TYPES.REMOVE_ITEM_FROM_CART,
								code: (item.variant as GetProductVariant).code,
							})
							return false
						}
					}) ?? [],
			}

			break
		case CART_ACTION_TYPES.UPDATE_ITEM_FROM_CART:
			const quantityUpdate = Number(action.payload.quantity)

			order = {
				...order,
				items: [
					...(state.order.items ?? []).map((item) => {
						if (item.id === action.payload.id) {
							pending.push({
								action: CART_ACTION_TYPES.UPDATE_ITEM_FROM_CART,
								code: (item.variant as GetProductVariant).code,
							})
							return {
								...item,
								quantity: quantityUpdate!,
								total: item?.unitPrice! * quantityUpdate!,
							}
						}
						return item
					}),
				],
			}

			break
		case CART_ACTION_TYPES.SET_CART:
			order = action.payload
			pending = []

			break
		default:
			return state
	}

	return {
		...state,
		order,
		itemsCount: getItemsCount(order),
		pending,
	}
}

const getItemsCount = (order: CartState['order']): number =>
	(order.items || []).reduce(
		(acc: number, item: GetOrderItem) => acc + (item.quantity || 0),
		0,
	) || 0

interface CartContextType {
	cartState: CartState
	cartDispatch: React.Dispatch<CartAction>
	cartT: TFunction<string, undefined>
}

const CartContext = React.createContext<CartContextType | undefined>(undefined)

interface CartProviderProps {
	children: React.ReactNode
	initialCart: CartState['order']
}

const CartProvider: React.FC<CartProviderProps> = ({
	children,
	initialCart,
}) => {
	const [state, dispatch] = React.useReducer(cartReducer, {
		order: initialCart,
		itemsCount: getItemsCount(initialCart),
		pending: [],
	})
	const { t } = useTranslation(CART_I18NEXT.routeCart)

	React.useEffect(() => {
		dispatch({
			type: CART_ACTION_TYPES.SET_CART,
			payload: initialCart,
		})
	}, [initialCart])

	return (
		<CartContext.Provider
			value={{ cartState: state, cartDispatch: dispatch, cartT: t }}
		>
			{children}
		</CartContext.Provider>
	)
}

const useCart = (): CartContextType => {
	const context = React.useContext(CartContext)
	if (!context) {
		throw new Error('useCart must be used within a CartProvider')
	}
	return context
}

export { CartProvider, useCart, CART_ACTION_TYPES }
